import React, { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { colors } from '@soluto-private/mx-asurion-ui-react';

import { useAnalytics } from '@service-initiation/shared-data-access-analytics';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { type ParsedStore } from '@service-initiation/tech-care-data-access-store';
import { formatNextAvailability } from '@service-initiation/tech-care-utils-date-time';

const Pointer = styled.button`
  position: absolute;
  min-width: 32px;
  min-height: 32px;
  left: -16px;
  bottom: 5px;
  background: ${colors.primaryDeep};
  border-radius: 16px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:after {
    position: absolute;
    bottom: -11px;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${colors.primaryDeep};
    content: '';
  }
`;

const StoreIndex = styled.div`
  color: ${colors.white};
  font-weight: bold;
  font-size: 1rem;
  text-align: center;
  line-height: normal;
`;

interface Props extends ParsedStore {
  expanded?: boolean;
  index: number;
  setFocusedStore: (store: ParsedStore) => void;
}

const TEXT = TranslationEntries.Repairs.StoreLocator.MapPointer;
const NEXT_AVAILABILITY_TEXT =
  TranslationEntries.Repairs.DateAndTime.NextAvailabilityFormat;

const MapPointer: FC<Props> = ({
  expanded,
  index,
  setFocusedStore,
  ...store
}: Props) => {
  const { dispatchClickEvent } = useAnalytics();
  const { t } = useTranslation();

  const nextAvailabilityText = useMemo(
    () =>
      formatNextAvailability(t, store.nextAvailability, NEXT_AVAILABILITY_TEXT),
    [store.nextAvailability, t]
  );

  const onPointerClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setFocusedStore(store);
      dispatchClickEvent('StoreDetails', {
        Cta: nextAvailabilityText || '',
        MapView: 'ExtendedDetails',
      });
    },
    [dispatchClickEvent, nextAvailabilityText, setFocusedStore, store]
  );

  return (
    <Pointer
      aria-label={t(TEXT.NumberPointer, { index, storeName: store.name })}
      data-testid="map-number-pointer"
      onClick={onPointerClick}
    >
      <StoreIndex>{index}</StoreIndex>
    </Pointer>
  );
};

export default MapPointer;
